import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField
} from '@mui/material';
import { colors } from '../../../themes/typography';
import { responsiveSpacing } from '../../../themes/responsive';
import { formatLocalDate } from '../../../utils/dateUtils';

const ExperimentFilters = ({
  filters,
  filterOptions,
  groupOptions,
  availableLocations,
  availableMachines,
  availableRoutes = [],
  availableAlertTypes = [],
  onFilterChange,
  onDateRangeChange,
  loading,
  filterMode,
  onFilterModeChange,
  productOptions,
  isAlertsMode = false,
  routesLoading = false
}) => {
  const { t } = useTranslation();

  const handleDateChange = (field, dateString) => {
    const date = dateString ? new Date(dateString) : null;
    onDateRangeChange({
      ...filters.dateRange,
      [field]: date
    });
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    return formatLocalDate(date);
  };

  const groupsList = Array.isArray(groupOptions) && groupOptions.length > 0
    ? groupOptions
    : filterOptions.groups || [];

  const selectRouteLabel = t('selectRoute', { defaultValue: 'Seleccionar ruta' });

  return (
    <Box sx={{ 
      mb: responsiveSpacing.xl,
      backgroundColor: colors.background.surface,
      border: `1px solid ${colors.border.light}`,
      borderRadius: '12px',
      p: responsiveSpacing.md
    }}>
      {/* Filter Mode Toggle intentionally hidden to permanently show machine view */}

      {/* Dynamic Filters */}
      <Box sx={{
        display: 'flex',
        gap: responsiveSpacing.md,
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        {filterMode === 'machine' ? (
          <>
            {/* Alert Type Filter - only in alerts mode */}
            {isAlertsMode && availableAlertTypes.length > 0 && (
              <FormControl size="small" sx={{ minWidth: { xs: 'unset', sm: 140 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>{t('alertType') || 'Type of Alert'}</InputLabel>
                <Select
                  value={filters.alertType}
                  label={t('alertType') || 'Type of Alert'}
                  onChange={(e) => onFilterChange('alertType', e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">{t('allTypes') || 'All Types'}</MenuItem>
                  {availableAlertTypes.map((alertType) => (
                    <MenuItem key={alertType} value={alertType}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {alertType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* Machine Mode Filters */}
            <FormControl size="small" sx={{ minWidth: { xs: 'unset', sm: 160 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>{t('route', { defaultValue: 'Ruta' })}</InputLabel>
              <Select
                value={filters.route}
                label={t('route', { defaultValue: 'Ruta' })}
                onChange={(e) => onFilterChange('route', e.target.value)}
                disabled={
                  loading ||
                  routesLoading ||
                  !filters.dateRange.start ||
                  !filters.dateRange.end ||
                  availableRoutes.length === 0
                }
              >
                <MenuItem value="">{selectRouteLabel}</MenuItem>
                {availableRoutes.map((route) => (
                  <MenuItem key={route.id} value={route.id}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {route.name || route.code || route.id}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 'unset', sm: 120 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>{t('group')}</InputLabel>
              <Select
                value={filters.group}
                label={t('group')}
                onChange={(e) => onFilterChange('group', e.target.value)}
                disabled={loading || groupsList.length === 0}
              >
                <MenuItem value="">{t('selectGroup')}</MenuItem>
                {groupsList?.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {group.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


            <FormControl size="small" sx={{ minWidth: { xs: 'unset', sm: 140 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>{t('location')}</InputLabel>
              <Select
                value={filters.location}
                label={t('location')}
                onChange={(e) => onFilterChange('location', e.target.value)}
                disabled={loading || !filters.group}
              >
                <MenuItem value="">{t('selectLocation')}</MenuItem>
                {availableLocations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {location.name.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 'unset', sm: 140 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>{t('machine')}</InputLabel>
              <Select
                value={filters.machine}
                label={t('machine')}
                onChange={(e) => onFilterChange('machine', e.target.value)}
                disabled={loading || !filters.location}
              >
                <MenuItem value="">{t('selectMachine')}</MenuItem>
                {availableMachines.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {machine.alias}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : null}

        {/* Date Range Filters - Always visible */}
        <TextField
          label={t('startDate')}
          type="date"
          size="small"
          value={formatDateForInput(filters.dateRange.start)}
          onChange={(e) => handleDateChange('start', e.target.value)}
          sx={{ minWidth: { xs: 'unset', sm: 140 }, width: { xs: '100%', sm: 'auto' } }}
          disabled={loading}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label={t('endDate')}
          type="date"
          size="small"
          value={formatDateForInput(filters.dateRange.end)}
          onChange={(e) => handleDateChange('end', e.target.value)}
          sx={{ minWidth: { xs: 'unset', sm: 140 }, width: { xs: '100%', sm: 'auto' } }}
          disabled={loading}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Box>
    </Box>
  );
};

export default ExperimentFilters;
